import { sortDates } from "@omnia/shared/utils/utils"
import { createLookup, FieldViewModel, Geometry2, LayerTypeId } from "../../../omnia"
import { IDataLayer, IDataLayerZone, INtrApp, INtrReq, NutrientType } from "../../../types"
import { getNutrientTypes } from "../../../utils"
import { INutritionMapViewContext } from "../types"
import { NutritionMapView } from "./view"
import { LayerViewModel } from "@omnia/shared/layers/layerViewModel"
import { DatalayerZone } from "@omnia/shared/layers/datalayerZone"
import { MapGeometryType } from "@omnia/shared/generated/enums"
import { RequirementsLayerViewerFactory } from "@omnia/shared/nutrition/map/requirementsLayerViewer"


export interface ITotalContribZone {
    ZoneNumber: number;
    Geometry: Geometry2;
    Area: number;
    Value: number;
}

export class TotalContributionsMapView extends NutritionMapView {

    static create(ctxt: INutritionMapViewContext) {

        let views = getNutrientTypes().map(nutrientType => {

            let view = new TotalContributionsMapView("Total contributions", "total-contrib", nutrientType, ctxt)
            view.setup()
            return view

        })

        return views

    }

    private static readonly layerTypeId = LayerTypeId.NutrientRequirements

    protected constructor(name: string, key: string, protected nutrientType: NutrientType, protected ctxt: INutritionMapViewContext) {
        super(name, key, nutrientType)
    }

    protected setup() {

        this._layerType = this.ctxt.nutritionSvc.getDataLayerType(TotalContributionsMapView.layerTypeId)

        let layerViewer = RequirementsLayerViewerFactory.createForNutrient(this.nutrientType, "")

        this._layerViewer = layerViewer

        this.setupColors(layerViewer.colors)

    }

    protected createLayers() {

        let nutrientKey = NutrientType[this._nutrient]

        const svc = this.ctxt.nutritionSvc

        const appLookup = createLookup(svc.getApplications(false), a => a.CroppingId)

        let ctxt = this.ctxt.datalayerContext
        let source = svc.getRequirementsProvider().getOrderedRequirementZones()

        const cropping = source.map(i => {
            return {
                cropping: this.ctxt.fieldSvc.findCropping(i.croppingId),
                plans: i.plans,
                applications: appLookup[i.croppingId] ?? []
            }
        }).filter(i => i.cropping && i.cropping.hasBoundary())

        let layerId = 0

        return cropping
            .sort((a, b) => sortDates(a.cropping.startDate, b.cropping.startDate))
            .map(i => {

                let layer = this.createCroppingDatalayer(++layerId, i.cropping, i.plans, i.applications, nutrientKey)

                if (layer) {

                    let vm = new LayerViewModel(ctxt, this.ctxt.map, layer, i.cropping.fieldVm)

                    return vm

                }

            }).filter(l => !!l)

    }

    /**
     * Returns total contribution zones from applications
     *
     * This only works if there is a single application contributing to the target nutrient
     * If there are multiple applications, returns undefined
     */
    protected getContributionZonesFromApplications(apps: INtrApp[], nutrientKey: string): ITotalContribZone[] {

        const appsForTargetNutrient = apps.filter(a => Boolean(a[nutrientKey]))

        const distinct = new Set(appsForTargetNutrient.map(a => a.AppId))

        if (distinct.size !== 1) {
            return
        }

        const singleAppId = appsForTargetNutrient[0].AppId

        apps = apps.filter(a => a.AppId == singleAppId)

        let zones = apps.map(a => ({
            ZoneNumber: a.ZoneNumber,
            Geometry: a.Geometry,
            Area: a.Area,
            Value: a[nutrientKey],
        }))

        if (zones.some(z => z.ZoneNumber > 0)) {
            zones = zones.filter(z => !!z.ZoneNumber)
        }

        return zones

    }

    /**
     * Returns total contribution zones from closing requirements
     *
     * "plans" provides the all the requirement zones, always starting with the initial requirements,
     * then with all applications, in application order
     * Contribution zones are calculated by simply looking at the difference between the first entry, and the last
     * Zones are matched/linked together using their initial nutrient-specific zone number
     * This only works if there have been no changes in the shapes of zones, which can be caused by a variable-rate
     * application contributing multiple nutrients
     */
    protected getContributionZones(plans: { planId: number, zones: INtrReq[] }[], nutrientKey: string): ITotalContribZone[] {

        let zoneNumberProvider: (req: INtrReq) => number

        switch (nutrientKey) {

            case "N":
            case "SO3":
                zoneNumberProvider = r => r.NZoneNumber
                break

            case "P2O5":
            case "K2O":
            case "MgO":
                zoneNumberProvider = r => r.PkZoneNumber
                break

            case "Lime":
                zoneNumberProvider = r => r.LimeZoneNumber
                break

            default:
                zoneNumberProvider = r => r.ZoneNumber
                break

        }

        let last = this.filterNutrientGroup(plans[plans.length - 1].zones)
        let first = this.filterNutrientGroup(plans[0].zones)

        let zones = last.map(lz => {

            let zoneNumber = zoneNumberProvider(lz) || lz.ZoneNumber || 0

            let fz = first.find(z => (z.ZoneNumber || 0) == zoneNumber)

            if (fz) {

                let reqKey = "Required" + nutrientKey
                let contrib = (fz[reqKey] || 0) - (lz[reqKey] || 0)

                return {
                    ZoneNumber: lz.ZoneNumber,
                    Geometry: lz.Geometry,
                    Area: lz.Area,
                    Value: contrib,
                }
            }

        }).filter(z => !!z)

        if (zones.some(z => z.ZoneNumber > 0)) {
            zones = zones.filter(z => !!z.ZoneNumber)
        }

        return zones

    }

    protected createCroppingDatalayer(layerId: number, cropping: FieldViewModel, plans: { planId: number, zones: INtrReq[] }[], applications: INtrApp[], nutrientKey: string): IDataLayer {

        /*
        * 221107 - Usersnap 2731
        * First, try getting contribution zones in the simple cases where only one application
        * has contributed the target nutrient
        **/
        let contrib = this.getContributionZonesFromApplications(applications, nutrientKey)

        /*
        * If the above did not work (contrib is undefined), then try by matching the first and last sets of requirement zones
        */
        contrib = contrib ?? this.getContributionZones(plans, nutrientKey)

        if (contrib.length) {
            const datalayer = this.createVirtualLayer(layerId, cropping, contrib)
            return datalayer

        }
    }

    protected createVirtualLayer(layerId: number, cropping: FieldViewModel, contrib: ITotalContribZone[]): IDataLayer {

        let zones: IDataLayerZone[] = contrib.map(r => {

            let value = r.Value || 0

            let name = r.ZoneNumber ? r.ZoneNumber.toString() : cropping.fieldName
            let geom = r.Geometry || cropping.cropping.Boundary

            let zone = <IDataLayerZone>{
                DataLayerZoneId: r.ZoneNumber,
                Number: r.ZoneNumber,
                Geometry: geom,
                Value: value,
                Name: name,
                Data: r
            }

            return zone

        })

        let layer: IDataLayer = {
            DataLayerId: layerId,
            GeometryType: MapGeometryType.Polygon,
            TypeId: this._layerType.TypeId,
            Type: this._layerType,
            FieldId: cropping.fieldId,
            CroppingId: cropping.croppingId,
            Zones: zones
        }

        return layer

    }

    protected applyZoneOverride(zone: DatalayerZone, ovr: any, refresh: boolean) {
        // N/A
    }

}
